import { connect, ConnectedProps } from 'react-redux';

import { RootState } from 'src/redux/store';
import { Bed, Room } from 'src/types/rooms';
import dataSelectors from 'src/redux/data/dataSelectors';
import { PatientState } from 'src/types/measurements';
import { selectors as roomsSelectors } from 'src/redux/data/rooms/modules/slice';
import {
  actions as monitorActions,
  selectors as monitorSelectors,
} from 'src/redux/data/monitor';
import {
  actions as idbActions,
  selectors as idbSelectors,
} from '../../modules/dashboard';
import { selectors as alertsSelectors } from 'src/redux/data/alerts';
import { selectors as loggedInUserSelectors } from 'src/redux/data/loggedInUser';
import { selectDeviceAlerts } from './utils';

const getRoom = (state: RootState): Room | undefined => {
  const roomsList = roomsSelectors.getRoomList(state);
  const deviceId = idbSelectors.getDeviceId(state);

  if (!deviceId) {
    return undefined;
  }

  const room = roomsList.find(room =>
    room.beds?.find(bed => bed.deviceId === deviceId),
  );

  return room;
};

const getBed = (state: RootState, room: Room | undefined): Bed | undefined => {
  const deviceId = idbSelectors.getDeviceId(state);

  if (!deviceId) {
    return undefined;
  }

  const bed = room?.beds?.find(bed => bed.deviceId === deviceId);

  return bed;
};

const isUnoccupied = (state: RootState) => {
  const currentPatientState = monitorSelectors.getCurrentPatientStateValue(
    state,
    idbSelectors.getDeviceId(state),
  );

  return (
    (!currentPatientState && currentPatientState !== 0) ||
    currentPatientState === PatientState.EMPTY
  );
};

const mapStateToProps = (state: RootState) => ({
  tenantId: loggedInUserSelectors.getCurrentTenantId(state),
  patient: idbSelectors.getPatientDetails(state),
  patientsList: dataSelectors.selectAssignedDevicePatients(state),
  saveRawData: idbSelectors.getIsSaveRawData(state),
  deviceId: idbSelectors.getDeviceId(state),
  selectedPatientId: idbSelectors.getCurrentPatientId(state),
  room: getRoom(state),
  isUnoccupied: isUnoccupied(state),
  bed: getBed(state, getRoom(state)),
  searchFocusStatus: idbSelectors.getSearchFocusStatus(state),
  isDeviceActive: monitorSelectors.getIsActive(
    state,
    idbSelectors.getDeviceId(state),
  ),
  isDeviceConnected: idbSelectors.getDeviceConnectionStatus(state),
  isStartContinuousLoading: monitorSelectors.getIsStartContinuousLoading(
    state,
    idbSelectors.getDeviceId(state),
  ),
  isStopContinuousLoading: monitorSelectors.getIsStopContinuousLoading(
    state,
    idbSelectors.getDeviceId(state),
  ),
  isVacancyAlertActive: alertsSelectors.selectIsBedExitAlertEnabled(
    state,
    idbSelectors.getCurrentPatientId(state) ?? '',
  ),
  isPositionChangeAlertActive:
    alertsSelectors.selectIsPositionChangeAlertEnabled(
      state,
      idbSelectors.getCurrentPatientId(state) ?? '',
    ),
  ongoingDeviceAlerts: selectDeviceAlerts(
    alertsSelectors.selectOngoingAlertsByPatientId(
      state,
      idbSelectors.getCurrentPatientId(state) ?? '',
    ),
  ),
  isLongOutOfBedAlertActive: alertsSelectors.selectLongOutOfBedAlertActive(
    state,
    idbSelectors.getCurrentPatientId(state) ?? '',
  ),
});

const mapActionCreators = {
  setSelectedPatientId: idbActions.setSelectedPatientId,
  onClickStartContinuous: monitorActions.onClickStartContinuous,
  onClickStopContinuous: monitorActions.onClickStopContinuous,
  onStartWithNoPatient: idbActions.setSearchFocusStatus,
  clearBedOccupancyGraph: idbActions.clearBedOccupancyGraph,
  getLatestSessions: idbActions.getLatestSessions,
};

const Connector = connect(mapStateToProps, mapActionCreators);

export type PropsFromRedux = ConnectedProps<typeof Connector>;
export default Connector;
